<script>
    import { onMount } from "svelte";
    import * as THREE from "three";
    import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
    import { gsap } from "gsap";
    import { ScrollTrigger } from "gsap/ScrollTrigger";
    import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
    import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";

    gsap.registerPlugin(ScrollTrigger);

    let container;
    let scene, camera, renderer, controls;
    let particles, particleGeometry;
    let shapes = [];
    let font;
    let colorBuffer;
    const clock = new THREE.Clock();
    let PARTICLE_COUNT = 3000;
    let webglSupported = true;
    let rafId;
    let lastScrollTime = 0;
    const SCROLL_DEBOUNCE = 200;
    let currentShapeIndex = 0;
    let isAnimating = false;

    // Add function to calculate particle count based on screen width
    function calculateParticleCount() {
        const baseCount = 3000;
        const minCount = 1500;
        const maxCount = 5000;
        const screenWidth = window.innerWidth;
        
        // Scale particle count based on screen width with a more aggressive scaling
        let count = Math.floor(baseCount * (screenWidth / 1280)); // Changed from 1920 to 1280 for more particles on smaller screens
        
        // Ensure count stays within bounds
        return Math.min(Math.max(count, minCount), maxCount);
    }

    onMount(async () => {
        if (!isWebGLAvailable()) {
            webglSupported = false;
            return;
        }

        try {
            // Calculate initial particle count
            PARTICLE_COUNT = calculateParticleCount();
            await init();
            animate();
            window.addEventListener("resize", onWindowResize);
        } catch (error) {
            console.error("Error initializing Three.js:", error);
            webglSupported = false;
        }

        return () => {
            window.removeEventListener("resize", onWindowResize);
            if (rafId) cancelAnimationFrame(rafId);
            if (renderer) {
                renderer.dispose();
                renderer.forceContextLoss();
            }
        };
    });

    function isWebGLAvailable() {
        try {
            const canvas = document.createElement("canvas");
            return !!(
                window.WebGLRenderingContext &&
                (canvas.getContext("webgl") ||
                    canvas.getContext("experimental-webgl"))
            );
        } catch (e) {
            return false;
        }
    }

    async function init() {
        // Scene setup
        scene = new THREE.Scene();
        scene.background = new THREE.Color(0x1a1a1a);
        scene.background.alpha = 0; // Make scene background transparent

        // Camera setup
        camera = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / window.innerHeight,
            0.1,
            1000,
        );
        camera.position.z = 5;
        camera.position.y = 1;

        // Renderer setup
        renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setClearColor(0x000000, 0);
        container.appendChild(renderer.domElement);

        // Controls setup
        controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.enableZoom = false;
        controls.enablePan = false;
        controls.enableRotate = false;

        // Load font
        const fontLoader = new FontLoader();
        font = await new Promise((resolve) => {
            fontLoader.load(
                "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
                resolve,
            );
        });

        // Create shapes
        await createShapes();

        // Create particle system
        createParticleSystem();

        // Setup scroll triggers
        setupScrollTriggers();
    }

    async function createShapes() {
        // ACM UMD Logo shape
        const acmShape = createTextShape("ACM UMD", 1);
        shapes.push(acmShape);
        shapes.push(acmShape);

        // Hack shape
        const hack = createTextShape(">_HACK...", 0.8);
        shapes.push(hack);

        // Infinity shape
        const infinityShape = createInfinityShape();
        shapes.push(infinityShape);

        // Hi shape
        const hi = createTextShape("01001000 01101001", 0.6);
        shapes.push(hi);

        // Heart shape
        const heartShape = createHeartShape();
        shapes.push(heartShape);

        // Final "Explore Tech" shape
        const exploreShape = createTextShape("Explore Tech", 0.8);
        shapes.push(exploreShape);
    }

    function createTextShape(text, size) {
        // Scale size based on screen width with increased base size
        const scaleFactor = Math.min(window.innerWidth / 1280, 2.0);
        const scaledSize = size * scaleFactor;

        const geometry = new TextGeometry(text, {
            font: font,
            size: scaledSize,
            height: 0.1,
            curveSegments: 24,
            bevelEnabled: false,
        });

        geometry.center();

        // Only convert to non-indexed if it's not already non-indexed
        const bufferGeo = geometry.index ? geometry.toNonIndexed() : geometry;
        const position = bufferGeo.getAttribute("position");

        const triangleCount = position.count / 3;
        const areas = [];

        // Step 1: compute area of each triangle
        for (let i = 0; i < triangleCount; i++) {
            const a = new THREE.Vector3().fromBufferAttribute(position, i * 3);
            const b = new THREE.Vector3().fromBufferAttribute(
                position,
                i * 3 + 1,
            );
            const c = new THREE.Vector3().fromBufferAttribute(
                position,
                i * 3 + 2,
            );

            const ab = new THREE.Vector3().subVectors(b, a);
            const ac = new THREE.Vector3().subVectors(c, a);
            const cross = new THREE.Vector3().crossVectors(ab, ac);

            const area = cross.length() * 0.5;
            areas.push(area);
        }

        // Step 2: create cumulative distribution for sampling
        const totalArea = areas.reduce((sum, a) => sum + a, 0);
        const cumulative = [];
        let accum = 0;
        for (let i = 0; i < areas.length; i++) {
            accum += areas[i] / totalArea;
            cumulative.push(accum);
        }

        // Step 3: pick triangles by area weight, sample points
        const points = [];

        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const r = Math.random();

            // Binary search for triangle index
            let low = 0,
                high = cumulative.length - 1;
            while (low < high) {
                const mid = Math.floor((low + high) / 2);
                if (r < cumulative[mid]) high = mid;
                else low = mid + 1;
            }

            const triangleIndex = low;

            const a = new THREE.Vector3().fromBufferAttribute(
                position,
                triangleIndex * 3,
            );
            const b = new THREE.Vector3().fromBufferAttribute(
                position,
                triangleIndex * 3 + 1,
            );
            const c = new THREE.Vector3().fromBufferAttribute(
                position,
                triangleIndex * 3 + 2,
            );

            // Barycentric coordinate sampling
            const r1 = Math.random();
            const r2 = Math.random();
            const sqrtR1 = Math.sqrt(r1);

            const point = new THREE.Vector3()
                .addScaledVector(a, 1 - sqrtR1)
                .addScaledVector(b, sqrtR1 * (1 - r2))
                .addScaledVector(c, sqrtR1 * r2);

            points.push(point.x, point.y, point.z);
        }

        return new Float32Array(points);
    }

    function createHeartShape() {
        const points = [];
        // Scale based on screen width with increased base size
        const scaleFactor = Math.min(window.innerWidth / 1280, 2.0); // Increased max scale from 1.5 to 2.0
        const scale = 0.25 * scaleFactor; // Increased from 0.2 to 0.25

        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const t = (i / PARTICLE_COUNT) * Math.PI * 2;

            const x = 16 * Math.pow(Math.sin(t), 3);
            const y =
                13 * Math.cos(t) -
                5 * Math.cos(2 * t) -
                2 * Math.cos(3 * t) -
                Math.cos(4 * t);
            const z = Math.sin(t * 2) * 0.5;

            points.push(x * scale, y * scale, z);
        }

        return new Float32Array(points);
    }

    function createInfinityShape() {
        const points = [];
        const loops = 2;
        // Scale based on screen width with increased base size
        const scaleFactor = Math.min(window.innerWidth / 1280, 2.0); // Increased max scale from 1.5 to 2.0
        const scale = 2.5 * scaleFactor; // Increased from 2 to 2.5

        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const t = (i / PARTICLE_COUNT) * Math.PI * 2 * loops;

            const x = scale * Math.sin(t);
            const y = scale * Math.sin(t) * Math.cos(t);
            const z = Math.sin(t * 3) * 0.5;

            points.push(x, y, z);
        }

        return new Float32Array(points);
    }

    function createParticleSystem() {
        const positions = new Float32Array(PARTICLE_COUNT * 3);
        const colors = new Float32Array(PARTICLE_COUNT * 3);

        // Calculate scale factor based on screen width with increased base size
        const scaleFactor = Math.min(window.innerWidth / 1280, 2.0); // Increased max scale from 1.5 to 2.0

        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const i3 = i * 3;

            // Random initial position in a scaled cube
            const cubeSize = 12 * scaleFactor; // Increased from 10 to 12
            positions[i3] = (Math.random() - 0.5) * cubeSize;
            positions[i3 + 1] = (Math.random() - 0.5) * cubeSize;
            positions[i3 + 2] = (Math.random() - 0.5) * cubeSize;

            // Alternate or random red/electric blue
            if (Math.random() < 0.5) {
                // Red
                colors[i3] = 1.0;
                colors[i3 + 1] = 0.0;
                colors[i3 + 2] = 0.0;
            } else {
                // Electric Blue
                colors[i3] = 0.0;
                colors[i3 + 1] = 0.6;
                colors[i3 + 2] = 1.0;
            }
        }

        // ✅ Correct: store reference after filling colors
        colorBuffer = colors;

        particleGeometry = new THREE.BufferGeometry();
        particleGeometry.setAttribute(
            "position",
            new THREE.BufferAttribute(positions, 3),
        );
        particleGeometry.setAttribute(
            "color",
            new THREE.BufferAttribute(colors, 3),
        );
        particleGeometry.setDrawRange(0, PARTICLE_COUNT);

        // Calculate particle size based on screen width
        const baseParticleSize = 0.1;
        const screenWidth = window.innerWidth;
        const particleSize = Math.max(baseParticleSize * (screenWidth / 1920), 0.05); // Minimum size of 0.05

        const material = new THREE.PointsMaterial({
            size: particleSize,
            vertexColors: true,
            transparent: true,
            opacity: 0.9,
            sizeAttenuation: true,
            blending: THREE.NormalBlending,
            depthWrite: false,
        });

        particles = new THREE.Points(particleGeometry, material);
        scene.add(particles);
        particles.position.y = 1.5;
    }

    function setupScrollTriggers() {
        const sections = document.querySelectorAll("section");

        // 🧠 Show the first shape immediately on load
        if (shapes[0]) {
            blendShapes(shapes[0], shapes[0], 1); // blend t = 1 means fully apply shapeA
        }

        sections.forEach((section, index) => {
            const shapeA = shapes[index];
            const shapeB = shapes[index + 1] || shapeA;

            ScrollTrigger.create({
                trigger: section,
                start: "top 80%",
                end: "top 40%",
                scrub: 1.5,
                onUpdate: (self) => {
                    const t = self.progress;
                    blendShapes(shapeA, shapeB, t);
                },
            });
        });
    }

    function blendShapes(shapeA, shapeB, t) {
        const positions = particleGeometry.attributes.position.array;

        for (let i = 0; i < positions.length; i++) {
            const a = shapeA[i % shapeA.length];
            const b = shapeB[i % shapeB.length];
            positions[i] = a + (b - a) * t;
        }

        particleGeometry.attributes.position.needsUpdate = true;
    }

    function morphToShape(targetIndex) {
        if (targetIndex < 0 || targetIndex >= shapes.length || isAnimating)
            return;
        isAnimating = true;
        currentShapeIndex = targetIndex;

        const positions = particleGeometry.attributes.position.array;
        const targetPositions = shapes[targetIndex];
        const from = new Float32Array(positions);

        // Direct morph to target shape
        const tempMorph = { t: 0 };
        gsap.to(tempMorph, {
            t: 1,
            duration: 0.5,
            ease: "power2.inOut",
            onUpdate: () => {
                for (let i = 0; i < positions.length; i++) {
                    const target =
                        targetPositions[i % targetPositions.length] || 0;
                    positions[i] = from[i] + (target - from[i]) * tempMorph.t;
                }
                particleGeometry.attributes.position.needsUpdate = true;
            },
            onComplete: () => {
                isAnimating = false;
            },
        });
    }

    function animate() {
        rafId = requestAnimationFrame(animate);

        const elapsed = clock.getElapsedTime();

        // Smooth oscillating rotation
        particles.rotation.y = Math.sin(elapsed * 0.9) * (Math.PI / 6);

        const colors = particleGeometry.attributes.color.array;

        // Oscillate between red and electric blue
        const t = (Math.sin(elapsed * 2.5) + 1) / 2; // blend factor (0 to 1)

        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const i3 = i * 3;

            // Linear interpolation: red ↔ electric blue
            colors[i3] = 1.0 * (1 - t) + 0.0 * t; // Red → 0
            colors[i3 + 1] = 0.0 * (1 - t) + 0.6 * t; // 0 → 0.6
            colors[i3 + 2] = 0.0 * (1 - t) + 1.0 * t; // 0 → 1.0
        }

        particleGeometry.attributes.color.needsUpdate = true;

        controls.update();
        renderer.render(scene, camera);
    }

    function onWindowResize() {
        // Recalculate particle count on resize
        PARTICLE_COUNT = calculateParticleCount();
        
        // Update camera and renderer
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
        
        // Recreate particle system with new count
        scene.remove(particles);
        createParticleSystem();
    }
</script>

<div class="particle-container" bind:this={container}>
    {#if !webglSupported}
        <div class="fallback"></div>
    {/if}
</div>

<style>
    .particle-container {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: -1;
        pointer-events: none;
        background: transparent;
        opacity: 0.95;
    }

    :global(canvas) {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        mix-blend-mode: screen;
    }

    .fallback {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(45deg, #1a1a1a, #2a2a2a);
        z-index: -1;
    }
</style>
